import { Tabs, TabsProps, Button, Skeleton } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

// components
import PageTitle from 'components/title/title';
import LoadingComponent from 'components/loadingComponent/loadingComponent';

// services
import { isRTL } from 'core/services/language';
import authService from 'core/services/auth';
import { onGetCompanyServices } from './commonServices';

// models
import { CompanyServiceKey } from './commonModels';

type ActionTab = NonNullable<TabsProps['items']>[number] & { link: string };

const ActionsPage: FunctionComponent = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const tabBarRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [companyServices, setCompanyServices] = useState<CompanyServiceKey[]>([]);
	const [isLoadingServices, setIsLoadingServices] = useState(true);

	useEffect(() => {
		const getCompanyServices = async () => {
			const response = await onGetCompanyServices();
			setCompanyServices(response?.services?.map((service) => service.key) ?? []);
			setIsLoadingServices(false);
		};

		getCompanyServices();
	}, []);

	const hasService = (key: CompanyServiceKey) => companyServices.includes(key);

	const tabItems = [
		hasService('shareholder_information') &&
			authService.isPermittedToAction('shareHolderInformationAction') && {
				id: '1',
				key: 'shareholder_information',
				link: 'shareholder-info',
				label: <h6>{t('ACTIONS.SHAREHOLDER_INFO')}</h6>,
				destroyInactiveTabPane: true,
			},
		hasService('registry_transaction') &&
			authService.isPermittedToAction('registryTransactionAction') && {
				id: '2',
				key: 'registry_transaction',
				link: 'registry-transactions',
				label: <h6>{t('ACTIONS.REGISTRY_TRANSACTIONS')}</h6>,
				destroyInactiveTabPane: true,
			},
		hasService('heirloom_transaction') &&
			authService.isPermittedToAction('heirloomTransactionAction') && {
				id: '3',
				key: 'heirloom_transaction',
				link: 'heirs-transfer',
				label: <h6>{t('ACTIONS.HEIRS_TRANSFER')}</h6>,
				destroyInactiveTabPane: true,
			},
		(hasService('general_suspend') || hasService('specific_suspend')) &&
			authService.isPermittedToAction('suspendAction') && {
				id: '4',
				key: 'general_suspend',
				link: 'suspend',
				label: <h6>{t('ACTIONS.SUSPEND')}</h6>,
				destroyInactiveTabPane: true,
			},
		hasService('notice') &&
			authService.isPermittedToAction('noticeAction') && {
				id: '5',
				key: 'notice',
				link: 'notice',
				label: <h6>{t('ACTIONS.NOTICE')}</h6>,
				destroyInactiveTabPane: true,
			},
		(hasService('announcements') || hasService('announcement_messages')) &&
			authService.isPermittedToAction('announcementAction') && {
				id: '6',
				key: 'announcement_messages',
				link: 'announcement',
				label: <h6>{t('ACTIONS.ANNOUNCEMENT')}</h6>,
				destroyInactiveTabPane: true,
			},
		hasService('update_company_documents') &&
			authService.isPermittedToAction('updateDocumentAction') && {
				id: '7',
				key: 'update_company_documents',
				link: 'update-documents',
				label: <h6>{t('ACTIONS.UPDATE_DOCUMENTS')}</h6>,
				destroyInactiveTabPane: true,
			},
		hasService('financial_statement') &&
			authService.isPermittedToAction('financialStatementAction') && {
				id: '8',
				key: 'financial_statement',
				link: 'financial-statement',
				label: <h6>{t('ACTIONS.FINANCIAL_STATEMENT')}</h6>,
				destroyInactiveTabPane: true,
			},
		hasService('assemblies') &&
			authService.isPermittedToAction('assemblyAction') && {
				id: '9',
				key: 'assemblies',
				link: 'assemblies',
				label: <h6>{t('ACTIONS.ASSEMBLIES')}</h6>,
				destroyInactiveTabPane: true,
			},
		hasService('board_meetings') &&
			(authService.isPermittedToAction('Board') || authService.isPermittedToAction('BoardMeeting')) && {
				id: '10',
				key: 'board_meetings',
				link: 'boards',
				label: <h6>{t('ACTIONS.BOARDS_AND_COMMITTEES')}</h6>,
				destroyInactiveTabPane: true,
			},
		(hasService('profit_capitalization') || hasService('treasury_shares_acquisition') || hasService('private_equity_issuance')) &&
			authService.isPermittedToAction('raiseCapital') && {
				id: '11',
				key: 'profit_capitalization',
				link: 'capitals',
				label: <h6>{t('ACTIONS.CAPITALS_MANAGEMENT')}</h6>,
				destroyInactiveTabPane: true,
			},
		hasService('dividends') &&
			authService.isPermittedToAction('dividends') && {
				id: '12',
				key: 'dividends',
				link: 'dividends',
				label: <h6>{t('ACTIONS.DIVIDENDS')}</h6>,
				destroyInactiveTabPane: true,
			},
		hasService('Loyalty') &&
			authService.isPermittedToAction('LoyaltyProgram') && {
				id: '13',
				key: 'loyalty-programs',
				link: 'loyalty-programs',
				label: <h6>{t('ACTIONS.LOYALTY_PROGRAMS')}</h6>,
				destroyInactiveTabPane: true,
			},
	].filter(Boolean) as ActionTab[];

	const getActiveTab = () => {
		const activeTab = tabItems.find((tab) => tab.link === location.pathname.split('/actions').pop()?.split('/')[1]);
		if (activeTab) return activeTab.key;
	};

	const getTransformValue = (element: HTMLElement): number => {
		const transform = window.getComputedStyle(element).transform;
		if (transform && transform !== 'none') {
			const matrix = transform.match(/matrix.*\((.+)\)/);
			if (matrix) {
				const values = matrix[1].split(', ');
				return parseFloat(values[4]) || 0; // translateX value
			}
		}
		return 0;
	};

	const checkScrollability = () => {
		const tabNavList = tabBarRef.current?.querySelector('.ant-tabs-nav-list') as HTMLElement;

		if (!tabNavList) {
			return;
		}

		const wrapWidth = tabBarRef.current?.offsetWidth || 0;
		const listWidth = tabNavList.offsetWidth;

		// Check if content overflows
		const hasOverflow = listWidth > wrapWidth;

		if (!hasOverflow) {
			setCanScrollLeft(false);
			setCanScrollRight(false);
			return;
		}

		const currentTransform = getTransformValue(tabNavList);
		const maxTransform = wrapWidth - listWidth;
		const threshold = 5;

		if (isRTL()) {
			// RTL: transform goes from 0 to positive
			setCanScrollLeft(currentTransform < -threshold);
			setCanScrollRight(currentTransform > maxTransform + threshold);
		} else {
			// LTR: transform goes from 0 to negative
			setCanScrollLeft(currentTransform < -threshold);
			setCanScrollRight(currentTransform > maxTransform + threshold);
		}
	};

	const scroll = (direction: 'left' | 'right') => {
		const tabNavWrap = tabBarRef.current?.querySelector('.ant-tabs-nav-wrap') as HTMLElement;
		const tabNavList = tabBarRef.current?.querySelector('.ant-tabs-nav-list') as HTMLElement;

		if (!tabNavWrap || !tabNavList) return;

		const scrollAmount = 300;
		const currentTransform = getTransformValue(tabNavList);
		const wrapWidth = tabBarRef.current?.offsetWidth || 0;
		const listWidth = tabNavList.offsetWidth;
		const maxTransform = wrapWidth - listWidth;

		let newTransform: number;

		if (direction === 'left') {
			newTransform = Math.min(currentTransform + scrollAmount, 0);
		} else {
			newTransform = Math.max(currentTransform - scrollAmount, maxTransform);
		}

		tabNavList.style.transition = 'transform 0.3s ease';
		tabNavList.style.transform = `translate(${newTransform}px, 0px)`;

		tabNavWrap.dispatchEvent(
			new WheelEvent('wheel', {
				deltaX: -(newTransform - currentTransform),
				bubbles: true,
				cancelable: true,
			}),
		);

		setTimeout(checkScrollability, 350);
	};

	useEffect(() => {
		if (!getActiveTab() && tabItems.length > 0) {
			navigate(`/dashboard/actions/${tabItems[0].link}`);
		}
	}, [location.pathname, tabItems, navigate]);

	useEffect(() => {
		// Multiple attempts to check scrollability
		const checkWithRetry = () => {
			checkScrollability();

			// Retry after short delays to catch late renders
			setTimeout(checkScrollability, 100);
			setTimeout(checkScrollability, 300);
			setTimeout(checkScrollability, 500);
		};

		checkWithRetry();

		// Watch for DOM changes
		const observer = new MutationObserver(() => {
			checkScrollability();
		});

		if (tabBarRef.current) {
			observer.observe(tabBarRef.current, {
				childList: true,
				subtree: true,
				attributes: true,
			});
		}

		// Watch for transform changes
		const tabNavList = tabBarRef.current?.querySelector('.ant-tabs-nav-list') as HTMLElement;
		if (tabNavList) {
			const transitionObserver = new MutationObserver(() => {
				checkScrollability();
			});
			transitionObserver.observe(tabNavList, {
				attributes: true,
				attributeFilter: ['style'],
			});
		}

		window.addEventListener('resize', checkScrollability);

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', checkScrollability);
		};
	}, [tabItems]);

	const ScrollButton = ({ direction }: { direction: 'left' | 'right' }) => {
		const canScroll = direction === 'left' ? canScrollLeft : canScrollRight;
		const Icon = direction === 'left' ? LeftOutlined : RightOutlined;

		return (
			<Button
				type="link"
				icon={<Icon />}
				onClick={() => scroll(direction)}
				disabled={!canScroll}
				style={{
					opacity: canScroll ? 1 : 0.3,
					cursor: canScroll ? 'pointer' : 'not-allowed',
					transform: isRTL() ? (direction === 'left' ? 'rotate(180deg)' : 'rotate(180deg)') : undefined,
				}}
			/>
		);
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			{isLoadingServices ? (
				<>
					<Skeleton.Button active block style={{ height: 40, marginBottom: 100, marginTop: 10 }} />
					<LoadingComponent />
				</>
			) : (
				<>
					<Tabs
						activeKey={getActiveTab()}
						items={tabItems.map(({ link, ...tab }) => tab)}
						size="large"
						className="customized"
						onTabClick={(key) => navigate(`/dashboard/actions/${tabItems.find((tab) => tab.key === key)?.link}`)}
						direction={isRTL() ? 'rtl' : 'ltr'}
						more={{ icon: null }}
						renderTabBar={(props, DefaultTabBar) => (
							<div ref={tabBarRef}>
								<DefaultTabBar
									{...props}
									extra={{
										left: <ScrollButton direction="left" />,
										right: <ScrollButton direction="right" />,
									}}
								/>
							</div>
						)}
					/>

					<Outlet />
				</>
			)}
		</>
	);
};

export default ActionsPage;
